import { GoogleGenerativeAI } from "@google/generative-ai";
import { VideoLength, StoryLanguage, StoryStructure } from '../types';
import { QuotaService } from '../services/quotaService';
import { CreditService } from '../services/creditService';

export interface StoryShotConfig {
  apiKey: string;  // Gemini API key for script generation
  googleCloudApiKey?: string;  // Google Cloud API key for video generation
  model?: string;
  videoModel?: string;
}

export interface StoryShotScript {
  content: string;
  scenes: Array<{
    scene: number;
    duration: string;
    visual: string;
    audio: string;
  }>;
}

export class StoryShot {
  private genAI: GoogleGenerativeAI;  // For script generation (Gemini API)
  private veoModel: any = null;  // For video generation (Google Cloud API)
  private config: StoryShotConfig;

  constructor(config: StoryShotConfig) {
    if (!config.apiKey) {
      throw new Error("Gemini API key is required for StoryShot initialization");
    }
    
    this.config = {
      model: 'gemini-1.5-flash',
      videoModel: 'veo-3.0-generate-preview',
      ...config
    };
    
    // Initialize Gemini API for script generation (AI Studio)
    this.genAI = new GoogleGenerativeAI(this.config.apiKey);
    console.log("Gemini API initialized for script generation");
    
    // Initialize Google Cloud API for video generation (if key provided)
    // Note: Client-side video generation is deprecated in favor of server-side API
    if (this.config.googleCloudApiKey) {
      this.veoModel = {
        apiKey: this.config.googleCloudApiKey,  // Use Google Cloud API key
        baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
        model: 'models/gemini-2.5-pro' // Gemini 2.5 Pro with Veo integration
      };
      console.log("Google Cloud API configured for video generation");
    }
    // No warning needed - video generation is handled server-side
  }

  /**
   * Generate a story script for YouTube Shorts with seamless 8-second segments
   */
  async generateScript(
    topic: string, 
    length: VideoLength | number, 
    language: StoryLanguage = StoryLanguage.ENGLISH,
    structure: StoryStructure = StoryStructure.HOOK_TO_END
  ): Promise<string> {
    // Handle custom length
    const totalSeconds = typeof length === 'number' ? length : parseInt(length);
    const isCustomLength = typeof length === 'number';
    const numSegments = isCustomLength ? 1 : totalSeconds / 8; // Custom length = single segment
    
    const languageNames: Record<StoryLanguage, string> = {
      [StoryLanguage.KOREAN]: 'Korean (한국어)',
      [StoryLanguage.ENGLISH]: 'English',
      [StoryLanguage.JAPANESE]: 'Japanese (日本語)',
      [StoryLanguage.CHINESE]: 'Chinese (中文)',
      [StoryLanguage.SPANISH]: 'Spanish (Español)',
      [StoryLanguage.FRENCH]: 'French (Français)',
      [StoryLanguage.GERMAN]: 'German (Deutsch)',
      [StoryLanguage.RUSSIAN]: 'Russian (Русский)',
    };

    const structureTemplates: Record<StoryStructure, string> = {
      [StoryStructure.HOOK_TO_END]: `
        - Start with an IRRESISTIBLE hook that creates immediate curiosity
        - Build tension progressively through each segment
        - Use cliffhangers at segment transitions to maintain attention
        - End with a satisfying payoff that rewards viewers for watching`,
      [StoryStructure.DRAMATIC_TWIST]: `
        - Set up expectations in the first segment
        - Build believable narrative in middle segments
        - Deliver SHOCKING twist in final moments
        - The twist must completely reframe everything shown before`,
      [StoryStructure.EMOTIONAL_JOURNEY]: `
        - Begin with relatable emotional situation
        - Build empathy and connection progressively
        - Reach emotional climax in final segment
        - Leave viewers with strong feelings (joy, inspiration, or tears)`,
      [StoryStructure.COMEDY_BUILDUP]: `
        - Start with amusing setup
        - Escalate comedy through unexpected developments
        - Build to hilarious climax
        - End with memorable punchline or callback`,
      [StoryStructure.MYSTERY_REVEAL]: `
        - Present intriguing mystery or question immediately
        - Drop hints and clues throughout segments
        - Build suspense and theories
        - Reveal surprising answer that makes perfect sense`,
      [StoryStructure.TUTORIAL_STEPS]: `
        - Clear introduction of what will be learned
        - Step-by-step progression through process
        - Visual demonstrations for each step
        - Final result showcase and recap`,
      [StoryStructure.BEFORE_AFTER]: `
        - Show problematic "before" state clearly
        - Document transformation process
        - Build anticipation for final result
        - Reveal dramatic "after" with impact`,
      [StoryStructure.CHALLENGE_RESULT]: `
        - Present the challenge or goal upfront
        - Show attempts and struggles
        - Build tension about success/failure
        - Reveal final outcome with celebration or lesson`,
      [StoryStructure.FACTS_SURPRISE]: `
        - Start with mind-blowing fact hook
        - Escalate with increasingly surprising information
        - Connect facts in unexpected ways
        - End with most shocking revelation`,
      [StoryStructure.STORY_LOOP]: `
        - Begin with intriguing scene
        - Develop story that seems linear
        - Reveal connection to beginning in final moment
        - Create "aha" moment where everything clicks`,
    };
    
    const prompt = isCustomLength ? `
      You are an expert scriptwriter specializing in viral YouTube Shorts.
      Your task is to generate a COMPLETE story script for a ${totalSeconds}-second video.

      **Video Topic:** "${topic}"
      **Total Story Length:** ${totalSeconds} seconds (single continuous video)
      **Language:** ${languageNames[language]} - ALL dialogue, narration, and text must be in ${languageNames[language]} ONLY
      
      **STORY STRUCTURE REQUIREMENTS:**
      ${structureTemplates[structure]}

      **CRITICAL REQUIREMENTS:**
      1. Create a COMPLETE story with beginning, middle, and end within ${totalSeconds} seconds
      2. This is a single continuous video without segments
      3. The pacing should be appropriate for a ${totalSeconds}-second video
      4. Maintain viewer engagement throughout the entire duration
      5. Structure the story naturally without forced segment breaks` : `
      You are an expert scriptwriter specializing in viral YouTube Shorts.
      Your task is to generate a COMPLETE story script that will be divided into ${numSegments} seamless 8-second segments.

      **Video Topic:** "${topic}"
      **Total Story Length:** ${totalSeconds} seconds (${numSegments} segments of 8 seconds each)
      **Language:** ${languageNames[language]} - ALL dialogue, narration, and text must be in ${languageNames[language]} ONLY
      
      **STORY STRUCTURE REQUIREMENTS:**
      ${structureTemplates[structure]}

      **CRITICAL REQUIREMENTS:**
      1. Create a COMPLETE story with beginning, middle, and end within ${totalSeconds} seconds
      2. The story must be divided into exactly ${numSegments} segments, each EXACTLY 8 seconds
      3. Each segment must END with a smooth transition that naturally leads to the next segment
      4. The segments when played sequentially must form a seamless, coherent story
      5. NO cliffhangers between segments - smooth narrative flow is essential
      
      **CHARACTER & SCENE CONTINUITY REQUIREMENTS:**
      1. **Character Consistency**: 
         - Use the SAME main character(s) throughout ALL segments
         - Maintain consistent character names, appearances, and personalities
         - Character development should be gradual and logical across segments
         
      2. **Scene Continuity**:
         - Each scene should logically flow from the previous one
         - Maintain consistent settings or show clear transitions between locations
         - Visual elements (props, backgrounds) should remain consistent or change logically
         
      3. **Narrative Thread**:
         - Single, continuous storyline across all segments
         - Each segment builds upon the previous one
         - No sudden jumps or disconnected scenes
      
      **Story Structure:**
      ${isCustomLength ? 
        `- Single continuous ${totalSeconds}-second video with natural story flow` :
        numSegments === 1 ? 
        '- Single 8-second segment: Complete micro-story with hook (2s), development (4s), conclusion (2s)' :
        numSegments === 2 ?
        '- Segment 1 (0-8s): Hook and setup\n- Segment 2 (8-16s): Development and conclusion' :
        '- Segment 1 (0-8s): Hook and initial setup\n- Segment 2 (8-16s): Main development and twist\n- Segment 3 (16-24s): Climax and conclusion'
      }

      **Output Format:**
      ${isCustomLength ? `
      Provide a complete script for the entire ${totalSeconds}-second video:
      
      FULL VIDEO (Time: 0-${totalSeconds}s)
      Scene: Complete Story
      Duration: ${totalSeconds}s` : `
      For each 8-second segment, provide:
      
      SEGMENT [number] (Time: [start]-[end]s)
      Scene: [scene number]
      Duration: 8s`}
      Visual: [Detailed visual description]
      Audio/Dialogue: [Script for voiceover/dialogue]
      Transition: [How this segment smoothly connects to the next, if applicable]

      ---
      IMPORTANT: Write ALL text, dialogue, and narration in ${languageNames[language]} language.
      
      ${language === StoryLanguage.KOREAN ? `
      EXAMPLE for a 16-second story in Korean:

      SEGMENT 1 (Time: 0-8s)
      Scene: 1
      Duration: 8s
      Visual: 요리사가 어지러운 주방에 들어와 충격받은 표정을 짓다가 빠르게 재료를 정리하기 시작한다.
      Audio/Dialogue: "저녁 서비스까지 30분! 이 주방은 엉망이야... 하지만 이 변화를 지켜봐!"
      Transition: 카메라가 요리사의 손이 첫 번째 팬을 잡으러 가는 것을 따라간다...

      SEGMENT 2 (Time: 8-16s)
      Scene: 2
      Duration: 8s
      Visual: ...손이 팬을 잡고, 요리하고 플레이팅하는 빠른 몽타주, 완벽한 요리의 마지막 샷.
      Audio/Dialogue: "썰고, 굽고, 담기 - 수년간의 훈련이 빛을 발한다. 혼돈에서 미슐랭 스타급 완벽함으로!"
      Transition: [마지막 세그먼트 - 전환 필요 없음]
      ` : `
      EXAMPLE for a 16-second story:

      SEGMENT 1 (Time: 0-8s)
      Scene: 1
      Duration: 8s
      Visual: A chef enters a messy kitchen, looks shocked, then starts organizing ingredients rapidly.
      Audio/Dialogue: "${language === StoryLanguage.JAPANESE ? '夕食サービスまで30分！このキッチンはめちゃくちゃだ...でもこの変化を見て！' : 
                        language === StoryLanguage.CHINESE ? '距离晚餐服务还有30分钟！这个厨房一团糟...但看看这个转变！' :
                        '30 minutes until dinner service! This kitchen is chaos... but watch this transformation!'}"
      Transition: Camera follows chef's hand reaching for the first pan...

      SEGMENT 2 (Time: 8-16s)
      Scene: 2
      Duration: 8s
      Visual: ...hand grabs pan, rapid montage of cooking, plating, final shot of perfect dishes.
      Audio/Dialogue: "${language === StoryLanguage.JAPANESE ? '切って、焼いて、盛り付け - 長年の訓練が活きる。混沌からミシュランスターの完璧さへ！' :
                        language === StoryLanguage.CHINESE ? '切、煎、装盘 - 多年训练的成果。从混乱到米其林星级的完美！' :
                        'Chop, sear, plate - years of training in action. From chaos to Michelin-star perfection!'}"
      Transition: [Final segment - no transition needed]
      `}

      ---

      Now generate the complete ${totalSeconds}-second story script with seamless 8-second segments.
    `;

    try {
      const model = this.genAI.getGenerativeModel({ model: this.config.model || "gemini-1.5-flash" });
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();
      
      // No tracking needed - Gemini API (AI Studio) is FREE for text generation
      console.log("Script generated using FREE Gemini API");
      
      return text;
    } catch (error) {
      console.error("Error generating script from Gemini API:", error);
      if (error instanceof Error) {
        throw new Error(`Failed to generate script. ${error.message}`);
      }
      throw new Error("An unknown error occurred while generating the script.");
    }
  }

  /**
   * Generate a video from script using Veo 3 API with quota management
   */
  async generateVideo(script: string): Promise<string> {
    console.log("Video generation requested for segment");
    
    try {
      // Extract visual and audio descriptions from the script
      const visualMatch = script.match(/Visual:\s*([^\n]+)/i);
      const audioMatch = script.match(/Audio\/Dialogue:\s*([^\n]+)/i);
      const segmentMatch = script.match(/SEGMENT\s+(\d+)/i);
      
      if (!visualMatch) {
        console.warn("No visual description found in script");
        return this.generatePlaceholder(script);
      }
      
      const visualDescription = visualMatch[1].trim();
      const audioDescription = audioMatch ? audioMatch[1].trim() : "";
      const segmentNumber = segmentMatch ? segmentMatch[1] : "1";
      
      // Check quota before attempting Veo generation
      const hasQuota = await QuotaService.hasQuota();
      if (!hasQuota) {
        console.warn("Daily Veo 3 quota exceeded. Remaining: 0");
        // Return quota exceeded indicator
        return this.generateQuotaExceededPlaceholder(segmentNumber);
      }
      
      // Try to generate video with Veo 3 API
      console.log("=== VEO 3 VIDEO GENERATION ATTEMPT ===");
      console.log("Veo 3 is in limited preview - attempting generation...");
      
      if (this.veoModel) {
        console.log("✅ Veo model exists");
        try {
          const remainingQuota = await QuotaService.getRemainingQuota();
          console.log("2. Remaining quota:", remainingQuota);
          
          // Create a simple prompt for Veo 3 (simplified for preview API)
          const veoPrompt = `${visualDescription} ${audioDescription}`;
          
          console.log("3. Generated Veo prompt:", veoPrompt);
          
          // Use quota
          console.log("4. Attempting to use quota...");
          const quotaUsed = await QuotaService.useQuota();
          if (!quotaUsed) {
            console.warn("❌ Failed to use quota - quota exceeded");
            return this.generateQuotaExceededPlaceholder(segmentNumber);
          }
          console.log("✅ Quota used successfully");
          
          // Start video generation with direct API call
          console.log("5. Making direct API call for video generation...");
          console.log("   Model:", this.veoModel.model);
          
          // Prepare the request for video generation with Gemini 2.0 Flash
          const requestBody = {
            contents: [{
              parts: [{
                text: veoPrompt  // Simple prompt for video generation
              }]
            }],
            generationConfig: {
              temperature: 0.8,
              topP: 0.95,
              candidateCount: 1,
              maxOutputTokens: 8192,
              responseModalities: ["VIDEO"],  // Specify video output modality
              videoGenerationConfig: {
                duration: "8s",  // 8 second video
                fps: 24,         // Frames per second
                aspectRatio: "9:16"  // Vertical format for Shorts
              }
            },
            safetySettings: [
              {
                category: "HARM_CATEGORY_HARASSMENT",
                threshold: "BLOCK_MEDIUM_AND_ABOVE"
              },
              {
                category: "HARM_CATEGORY_HATE_SPEECH", 
                threshold: "BLOCK_MEDIUM_AND_ABOVE"
              },
              {
                category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
                threshold: "BLOCK_MEDIUM_AND_ABOVE"
              },
              {
                category: "HARM_CATEGORY_DANGEROUS_CONTENT",
                threshold: "BLOCK_MEDIUM_AND_ABOVE"
              }
            ]
          };
          
          console.log("Request body prepared:", JSON.stringify(requestBody, null, 2));
          
          // Make the API call - using generateContent endpoint for Gemini 2.0 Flash
          const apiUrl = `${this.veoModel.baseUrl}/${this.veoModel.model}:generateContent?key=${this.veoModel.apiKey}`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
          });
          
          console.log("API Response status:", response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error("API Error Response:", errorText);
            throw new Error(`API call failed: ${response.status} - ${errorText}`);
          }
          
          const result = await response.json();
          console.log("✅ API Response received:", JSON.stringify(result, null, 2));
          
          // Check if the response contains video data
          if (result.candidates && result.candidates[0]) {
            const candidate = result.candidates[0];
            
            // Check for video content in the response
            if (candidate.content && candidate.content.parts) {
              for (const part of candidate.content.parts) {
                // Check for video data in various possible formats
                
                // 1. Check for inline video data (base64 encoded)
                if (part.inlineData && part.inlineData.mimeType && part.inlineData.mimeType.startsWith('video/')) {
                  console.log("✅ VIDEO GENERATED SUCCESSFULLY!");
                  console.log("   Video data found in response (inline)");
                  const remainingAfter = await QuotaService.getRemainingQuota();
                  console.log("   Remaining quota after generation:", remainingAfter);
                  
                  // Track video usage for credit calculation
                  CreditService.trackUsage('video', 8); // 8 seconds video
                  
                  return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                } 
                
                // 2. Check for file reference (Google Cloud Storage)
                else if (part.fileData && part.fileData.mimeType && part.fileData.mimeType.startsWith('video/')) {
                  console.log("✅ VIDEO GENERATED SUCCESSFULLY!");
                  console.log("   Video file reference found:", part.fileData.fileUri);
                  const remainingAfter = await QuotaService.getRemainingQuota();
                  console.log("   Remaining quota after generation:", remainingAfter);
                  
                  // Track video usage for credit calculation
                  CreditService.trackUsage('video', 8); // 8 seconds video
                  
                  return part.fileData.fileUri;
                } 
                
                // 3. Check for video URI directly in the part
                else if (part.videoUri) {
                  console.log("✅ VIDEO GENERATED SUCCESSFULLY!");
                  console.log("   Video URI found:", part.videoUri);
                  const remainingAfter = await QuotaService.getRemainingQuota();
                  console.log("   Remaining quota after generation:", remainingAfter);
                  
                  return part.videoUri;
                }
                
                // 4. Check for video metadata
                else if (part.videoMetadata) {
                  console.log("✅ VIDEO GENERATED SUCCESSFULLY!");
                  console.log("   Video metadata found:", part.videoMetadata);
                  const remainingAfter = await QuotaService.getRemainingQuota();
                  console.log("   Remaining quota after generation:", remainingAfter);
                  
                  // Try to extract video URL from metadata
                  if (part.videoMetadata.uri) {
                    return part.videoMetadata.uri;
                  } else if (part.videoMetadata.url) {
                    return part.videoMetadata.url;
                  } else {
                    // Return metadata as JSON if no direct URL
                    return `veo:${JSON.stringify(part.videoMetadata)}`;
                  }
                }
                
                // 5. If text response, log it
                else if (part.text) {
                  console.log("Got text response instead of video:", part.text);
                }
              }
            }
            
            // If no video found in response, it returned text
            console.warn("⚠️ No video data in response, model returned text instead");
            console.warn("The model might not support video generation yet");
            
            // Restore quota since no video was generated
            const quota = await QuotaService.getQuota();
            quota.used = Math.max(0, quota.used - 1);
            localStorage.setItem('veo3_daily_quota', JSON.stringify(quota));
            console.log("   Quota restored");
            
            // Fall back to storyboard
            throw new Error("Model returned text instead of video - video generation not available");
          }
          
        } catch (veoError) {
          console.error("❌ VEO 3 API ERROR CAUGHT!");
          console.error("Error type:", veoError instanceof Error ? veoError.constructor.name : typeof veoError);
          console.error("Error message:", veoError instanceof Error ? veoError.message : String(veoError));
          
          if (veoError instanceof Error && veoError.stack) {
            console.error("Stack trace:", veoError.stack);
          }
          
          // Check if it's an API key or authentication error
          const errorMessage = veoError instanceof Error ? veoError.message : '';
          if (errorMessage.includes('API key') || errorMessage.includes('401') || errorMessage.includes('403')) {
            console.error("🔑 API KEY ISSUE DETECTED!");
            console.error("Make sure you have:");
            console.error("1. Valid Gemini API key");
            console.error("2. Veo 3 access enabled (paid subscription)");
            console.error("3. Correct API key in environment variables");
          }
          
          if (errorMessage.includes('models.generateVideos')) {
            console.error("📹 VEO API METHOD ERROR!");
            console.error("The generateVideos method might not be available");
            console.error("This could mean:");
            console.error("1. Wrong API package version");
            console.error("2. Veo 3 not available in your region");
            console.error("3. API changes or deprecation");
          }
          
          // Fall back to storyboard generation instead of throwing
          console.warn("⚠️ FALLING BACK TO STORYBOARD GENERATION");
        }
      } else {
        console.error("❌ VEO MODEL NOT INITIALIZED!");
        console.error("This usually means:");
        console.error("1. API key is missing");
        console.error("2. Veo model is not available");
        console.error("3. Constructor error during initialization");
      }
      
      /* The following code was the original fallback, but is no longer needed */
      
      // Fallback: Generate detailed storyboard with image generation prompts using Gemini
      const storyboardPrompt = `
        You are a professional video director and AI image generation expert. Create a detailed 8-second video storyboard with 4 keyframes (one every 2 seconds).
        
        Scene Description:
        Visual: ${visualDescription}
        Audio/Dialogue: ${audioDescription}
        
        For each of the 4 keyframes, provide:
        1. Exact frame description (camera angle, composition, characters, objects, actions)
        2. Image generation prompt (detailed prompt for AI image generation, including style, lighting, colors)
        3. Color palette (main colors in hex format)
        4. Lighting description
        5. Motion/animation notes (how this frame transitions to the next)
        6. Sound effects at that moment
        
        IMPORTANT: Make the image prompts very detailed and suitable for AI image generation (like Stable Diffusion, DALL-E, or Imagen).
        
        Format your response as JSON with this structure:
        {
          "segment": ${segmentNumber},
          "duration": "8s",
          "frames": [
            {
              "time": "0s",
              "description": "...",
              "imagePrompt": "Detailed prompt for AI image generation...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            },
            {
              "time": "2s",
              "description": "...",
              "imagePrompt": "...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            },
            {
              "time": "4s",
              "description": "...",
              "imagePrompt": "...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            },
            {
              "time": "6s",
              "description": "...",
              "imagePrompt": "...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            }
          ],
          "style": "YouTube Shorts, 9:16 aspect ratio, vibrant and engaging, photorealistic",
          "veoPrompt": "A single prompt for the entire 8-second video that could be used with Veo API"
        }
      `;
      
      try {
        const model = this.genAI.getGenerativeModel({ 
          model: "gemini-1.5-flash",
          generationConfig: {
            temperature: 0.7,
            topP: 0.8,
            maxOutputTokens: 3000,
            responseMimeType: "application/json"
          }
        });
        
        const result = await model.generateContent(storyboardPrompt);
        const response = await result.response;
        const storyboardText = response.text();
        
        // Try to parse the JSON response
        let storyboardData;
        try {
          // Try direct JSON parse first
          storyboardData = JSON.parse(storyboardText);
          console.log("Generated storyboard with image prompts:", JSON.stringify(storyboardData, null, 2).substring(0, 500) + "...");
          
          // Store the Veo prompt for future use when API becomes available
          if (storyboardData.veoPrompt) {
            console.log("Veo prompt generated:", storyboardData.veoPrompt);
          }
          
        } catch (parseError) {
          // Try to extract JSON from markdown code blocks
          const jsonMatch = storyboardText.match(/```json\n?([\s\S]*?)\n?```/) || 
                           storyboardText.match(/\{[\s\S]*\}/);
          
          if (jsonMatch) {
            const jsonStr = jsonMatch[1] || jsonMatch[0];
            storyboardData = JSON.parse(jsonStr);
          } else {
            console.warn("Could not parse storyboard JSON, using fallback");
            storyboardData = {
              segment: segmentNumber,
              description: storyboardText,
              visual: visualDescription,
              audio: audioDescription,
              frames: []
            };
          }
        }
        
        // Create an enhanced video placeholder with storyboard data
        return this.generateStoryboardVideo(storyboardData);
        
      } catch (error) {
        console.error("Error generating storyboard:", error);
        return this.generateEnhancedPlaceholder(visualDescription, audioDescription, "");
      }
      
    } catch (error) {
      console.error("Error in video generation:", error);
      return this.generatePlaceholder(script);
    }
  }
  
  /**
   * Generate a storyboard-based video placeholder
   */
  private generateStoryboardVideo(storyboardData: any): string {
    const timestamp = Date.now();
    const data = {
      type: "storyboard_video",
      segment: storyboardData.segment || 1,
      frames: storyboardData.frames || [],
      style: storyboardData.style || "YouTube Shorts",
      duration: storyboardData.duration || "8s",
      timestamp: timestamp
    };
    
    // Encode the storyboard data as base64
    const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    return `data:video/storyboard;base64,${encodedData}`;
  }
  
  /**
   * Generate a basic placeholder for video
   */
  private generatePlaceholder(script: string): string {
    const timestamp = Date.now();
    const segmentHash = this.hashCode(script.substring(0, 100));
    return `data:video/placeholder;base64,${btoa(`video-${segmentHash}-${timestamp}`)}`;
  }
  
  /**
   * Generate an enhanced placeholder with description
   */
  private generateEnhancedPlaceholder(visual: string, audio: string, detailed: string): string {
    const timestamp = Date.now();
    const data = {
      type: "video_placeholder",
      visual: visual.substring(0, 100),
      audio: audio.substring(0, 100),
      detailed: detailed.substring(0, 200),
      timestamp: timestamp
    };
    return `data:video/placeholder;base64,${btoa(JSON.stringify(data))}`;
  }
  
  /**
   * Generate quota exceeded placeholder
   */
  private generateQuotaExceededPlaceholder(segmentNumber: string): string {
    const data = {
      type: "quota_exceeded",
      segment: segmentNumber,
      message: "일일 Veo 3 무료 할당량이 소진되었습니다",
      timestamp: Date.now()
    };
    return `data:video/quota_exceeded;base64,${btoa(JSON.stringify(data))}`;
  }
  
  /**
   * Get remaining quota
   */
  static async getRemainingQuota(): Promise<number> {
    return await QuotaService.getRemainingQuota();
  }
  
  /**
   * Simple hash function for generating unique IDs
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Parse script into structured format
   */
  parseScript(scriptText: string): StoryShotScript {
    const scenes = [];
    const sceneRegex = /Scene:\s*(\d+)\s*Duration:\s*([^\n]+)\s*Visual:\s*([^\n]+)\s*Audio\/Dialogue:\s*([^\n]+)/gi;
    
    let match;
    while ((match = sceneRegex.exec(scriptText)) !== null) {
      scenes.push({
        scene: parseInt(match[1]),
        duration: match[2].trim(),
        visual: match[3].trim(),
        audio: match[4].trim()
      });
    }

    return {
      content: scriptText,
      scenes
    };
  }
}

// Export singleton factory
export function createStoryShot(apiKey: string): StoryShot {
  return new StoryShot({ apiKey });
}